import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { DatePicker } from "@/components/atoms/DatePicker";
import Button from "@/components/atoms/Button";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { useState } from "react";

interface ExportModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateExport: (exportFilters: { field_name: string; start_date: Date; end_date: Date }) => void;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onOpenChange, onCreateExport }) => {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedOption, setSelectedOption] = useState<string>("created_on");

  const handleCreateExport = () => {
    if (selectedOption && startDate && endDate) {
      onCreateExport({ field_name: selectedOption, start_date: startDate, end_date: endDate });

      setStartDate(undefined);
      setEndDate(undefined);
      setSelectedOption("created_on");
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedOption("created_on");
      setStartDate(undefined);
      setEndDate(undefined);
    }

    onOpenChange(open);
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 bg-black/50 dark:bg-black/60 z-40 transition-opacity" />
      <Dialog.Content className="fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full transition-colors">
          <Dialog.Title className="text-lg font-bold">Create Export</Dialog.Title>
          <Dialog.Description className="text-sm text-gray-500 mb-4">
            The export reflects your selected filters and the chosen time range.
          </Dialog.Description>

          <RadioGroup.Root value={selectedOption} onValueChange={setSelectedOption} className="flex flex-col gap-2.5">
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center">
              <RadioGroup.Item
                className="w-6 h-6 cursor-pointer rounded-full bg-gray-200 dark:bg-gray-700 shadow-md outline-none hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 data-[state=checked]:bg-primary"
                value="created_on"
                id="created_on"
              >
                <RadioGroup.Indicator className="flex items-center justify-center w-full h-full rounded-full bg-blue-500" />
              </RadioGroup.Item>
              <label htmlFor="created_on" className="pl-3 text-sm leading-none text-gray-900 dark:text-gray-100 cursor-pointer">
                Created On
              </label>
            </div>

            <div className="flex items-center">
              <RadioGroup.Item
                className="w-6 h-6 cursor-pointer rounded-full bg-gray-200 dark:bg-gray-700 shadow-md outline-none hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 data-[state=checked]:bg-primary"
                value="closed_on"
                id="closed_on"
              >
                <RadioGroup.Indicator className="flex items-center justify-center w-full h-full rounded-full bg-blue-500" />
              </RadioGroup.Item>
              <label htmlFor="closed_on" className="pl-3 text-sm leading-none text-gray-900 dark:text-gray-100 cursor-pointer">
                Closed On
              </label>
            </div>  
            </div>
          </RadioGroup.Root>

          <div>
            <DatePicker placeholder="Start Date" value={startDate} onChange={setStartDate} />
          </div>
          
          <div className="mt-4">
            <DatePicker placeholder="End Date" value={endDate} onChange={setEndDate} />
          </div>
            


          <div className="flex justify-end mt-4 space-x-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateExport}>Create Export</Button>
          </div>

          <Dialog.Close asChild>
            <button
              className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full text-gray-500 hover:text-black"
              aria-label="Close"
            >
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ExportModal;